import Label from './Label';
import React, { useContext, useEffect, useMemo } from 'react';
import V5GadgetFormContext from '../../contexts/V5GadgetFormContext';
import Dropdown from '../Inputs/Dropdown';
import Inputs from '../Inputs';
import MetricOptionsContext from '../../contexts/MetricOptionsContext';
import Row from '../Common/Row';
import limitModeTypeCheckers from './limitModeTypeCheckers';

const SortDropdown = () => {
  const { series, groupByLimitMode, setGroupByLimitMode, dimensionA } =
    useContext(V5GadgetFormContext);
  const { metricOptions } = useContext(MetricOptionsContext);

  if (!limitModeTypeCheckers.isExcludeTopN(groupByLimitMode)) {
    const e = new Error(
      'Sort dropdown should only be rendered when Exclude Top N is selected',
    );
    e.name = 'SortDropdownError';
    throw e;
  }

  const sortFieldName = useMemo(() => {
    const sortConfig = groupByLimitMode.excludeSort;

    if (!sortConfig) {
      return undefined;
    }

    let label;
    series.forEach((s) => {
      const metricName = metricOptions.find((m) => m.id === s.metricId)?.name;

      if (s.metricId === sortConfig.field) {
        label = metricName;
      } else if (sortConfig.field?.includes(s.metricId)) {
        label = sortConfig.field.replace(`${s.metricId}-`, `${metricName} (`);
        label = label + ')';
      }
    });

    // If the sort field is a metric, return the metric name
    if (label) {
      return label;
    } else {
      return sortConfig.field;
    }
  }, [groupByLimitMode.excludeSort, metricOptions, series]);

  const dropdownOptions = useMemo(() => {
    const options: DropdownOption[] = [];
    series.forEach((s) => {
      const metricName = metricOptions.find((m) => m.id === s.metricId)?.name;

      if (!metricName) {
        return;
      }

      const metricOption = {
        label: metricName,
        onSelected: () =>
          setGroupByLimitMode({
            ...groupByLimitMode,
            excludeSort: {
              ...groupByLimitMode.excludeSort,
              field: s.metricId,
              isComparison: false,
              metricId: s.metricId,
              isAbsolute: false,
              direction: groupByLimitMode.excludeSort?.direction || 'desc',
            },
          }),
      };
      options.push(metricOption);
    });

    if (dimensionA) {
      options.push({
        label: dimensionA.field,
        onSelected: () => {
          setGroupByLimitMode({
            ...groupByLimitMode,
            excludeSort: {
              field: dimensionA.field,
              direction: groupByLimitMode.excludeSort?.direction || 'desc',
              isComparison: false,
              metricId: undefined,
              isAbsolute: false,
            },
          });
        },
      });
    }

    return options;
  }, [
    dimensionA,
    groupByLimitMode,
    metricOptions,
    series,
    setGroupByLimitMode,
  ]);

  return <Dropdown selectedLabel={sortFieldName} options={dropdownOptions} />;
};

const ExcludeInputs = ({
  groupByLimitMode,
}: {
  groupByLimitMode: ExcludeTopN;
}) => {
  const { series, dimensionA, setGroupByLimitMode } =
    useContext(V5GadgetFormContext);

  const usedMetricIds = useMemo(() => {
    return series.map((s) => s.metricId);
  }, [series]);

  const usedMetricComparisons = useMemo(() => {
    const comparisons: string[] = [];
    series.forEach((s) => {
      if (s.matrixCellType === 'delta' || s.matrixCellType === 'percentDelta') {
        comparisons.push(`${s.metricId}-${s.matrixCellType}`);
      } else if (s.matrixCellType === 'both') {
        comparisons.push(`${s.metricId}-delta`);
        comparisons.push(`${s.metricId}-percentDelta`);
      }
    });
    return comparisons;
  }, [series]);

  // If the display or exclude sort field is invalid, reset it to the default.
  useEffect(() => {
    const displaySortField = groupByLimitMode.displaySort?.field;
    const displaySortMetricId = groupByLimitMode.displaySort?.metricId;
    const hasInvalidDisplaySortField =
      displaySortField &&
      displaySortField !== dimensionA?.field &&
      displaySortMetricId &&
      !usedMetricIds.includes(displaySortField) &&
      !usedMetricComparisons.includes(displaySortField);

    const excludeSortField = groupByLimitMode.excludeSort?.field;
    const excludeSortMetricId = groupByLimitMode.displaySort?.metricId;
    const hasInvalidExcludeSortField =
      excludeSortField &&
      excludeSortMetricId &&
      excludeSortField !== dimensionA?.field &&
      !usedMetricIds.includes(excludeSortMetricId);

    if (hasInvalidDisplaySortField || hasInvalidExcludeSortField) {
      console.warn(
        "Configured display or exclude field doesn't exist in the series.",
      );
      setGroupByLimitMode({
        ...groupByLimitMode,
        displaySort: hasInvalidDisplaySortField
          ? undefined
          : groupByLimitMode.displaySort,
        excludeSort: hasInvalidExcludeSortField
          ? undefined
          : groupByLimitMode.excludeSort,
      });
    }
  }, [
    dimensionA?.field,
    groupByLimitMode,
    series,
    setGroupByLimitMode,
    usedMetricComparisons,
    usedMetricIds,
  ]);

  return (
    <>
      <div>
        <Label>Exclude Sort</Label>
        <Row style={{ flexWrap: 'wrap' }}>
          <SortDropdown />
          <div style={{ marginBottom: 8 }}>
            <Dropdown
              selectedLabel={groupByLimitMode.excludeSort?.direction || 'desc'}
              options={[
                {
                  label: 'asc',
                  onSelected: () =>
                    setGroupByLimitMode({
                      ...groupByLimitMode,
                      excludeSort: {
                        ...groupByLimitMode.excludeSort,
                        direction: 'asc',
                      },
                    }),
                },
                {
                  label: 'desc',
                  onSelected: () =>
                    setGroupByLimitMode({
                      ...groupByLimitMode,
                      excludeSort: {
                        ...groupByLimitMode.excludeSort,
                        direction: 'desc',
                      },
                    }),
                },
              ]}
            />
          </div>
        </Row>
      </div>
      <div style={{ marginBottom: 8 }}>
        <Label>Exclude Amount</Label>
        <Inputs.TextInput
          type="number"
          value={groupByLimitMode.n ? groupByLimitMode.n.toString(10) : '0'}
          onChange={(e) => {
            setGroupByLimitMode({
              ...groupByLimitMode,
              n: Number(e.target.value),
            });
          }}
        />
      </div>
    </>
  );
};

const Gate = () => {
  const { groupByLimitMode } = useContext(V5GadgetFormContext);

  if (!limitModeTypeCheckers.isExcludeTopN(groupByLimitMode)) {
    return null;
  }

  return <ExcludeInputs groupByLimitMode={groupByLimitMode} />;
};

export default Gate;
